import { useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "./apiClient";

interface IParams {
  start_date: string;
  end_date: string;
  branches?: number[];
  /** Purchase events (and similar) with no branch_id */
  include_company_level_payments?: boolean;
  enabled?: boolean;
}

export interface IItemFluctuationDataPayload {
  report_name: string;
  period: {
    start_date: string;
    end_date: string;
    label: string;
  };
  branches: string[];
  items: {
    item_id: number;
    item_name: string;
    sku: string;
    fluctuation_percent: number;
  }[];
}

export interface IItemFluctuationDataResponse {
  data: IItemFluctuationDataPayload;
}

export interface IPriceFluctuationDetailPayload {
  report_name: string;
  period: {
    start_date: string;
    end_date: string;
    label: string;
  };
  branches: string[];
  rows: {
    purchase_id: number;
    purchase_ref: string;
    branch_name: string;
    due_date: string;
    item_name: string;
    unit_name: string;
    quantity: number;
    net_total: number;
    fluctuation_percent: number;
  }[];
}

export interface IPriceFluctuationDetailResponse {
  data: IPriceFluctuationDetailPayload;
}

interface IPriceFluctuationDataParams extends IParams {
  item_id: string;
}

/** JSON for Item Detail Report (purchase lines + fluctuation %). */
export const useGetPriceFluctuationData = ({
  enabled,
  ...params
}: IPriceFluctuationDataParams) =>
  useQuery<any, APIErrorResponse, IPriceFluctuationDetailResponse>({
    queryKey: [
      "get-price-fluctuation-data",
      params.start_date,
      params.end_date,
      params.item_id,
      params.branches,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      searchParams.set("item_id", params.item_id);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/price-fluctuation-data?${searchParams.toString()}`
      );
    },
    enabled: enabled ?? false,
    gcTime: 0,
  });

export const useGetOCReport = ({ enabled, ...rest }: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-OC-3rd-eye-report",
      rest.start_date,
      rest.end_date,
      (rest.branches ?? []).join(","),
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/operation-cost-report`, {
        params: rest,
      });
    },
    enabled: enabled,
    gcTime: 0,
  });

export type OperationCostReportRow = {
  expense: string;
  amount: number;
  type: string;
  type_key: "fixed" | "asset" | "variable";
};

export type OperationCostReportDataPayload = {
  report_name: string;
  period: { start_date: string | null; end_date: string | null };
  branches: { id: number; name: string }[];
  rows: OperationCostReportRow[];
  total_oc: number;
};

type OperationCostDataParams = IParams & { enabled?: boolean };

export const useGetOperationCostReportData = (params: OperationCostDataParams) =>
  useQuery<any, APIErrorResponse, { data: OperationCostReportDataPayload }>({
    queryKey: [
      "get-operation-cost-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/operation-cost-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetPaymentReport = ({ enabled, ...rest }: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-payment-3rd-eye-report", rest.start_date, rest.end_date],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/payment-report`, {
        params: rest,
      });
    },
    enabled: enabled,
    gcTime: 0,
  });

export const useGetInventoryReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-inventory-3rd-eye-report",
      params.start_date,
      params.end_date,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/inventory-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export const useGetPurchaseReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-purchase-3rd-eye-report",
      params.start_date,
      params.end_date,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/purchase-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export const useGetTaxReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-tax-3rd-eye-report", params.start_date, params.end_date],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/tax-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export type TaxReportRowData = {
  kind: "data";
  id: string;
  label: string;
  cells: (number | null)[];
  total: number;
  account_bold: boolean;
  total_bold: boolean;
};

export type TaxReportRowSection = {
  kind: "section";
  id: string;
  label: string;
};

export type TaxReportRow = TaxReportRowData | TaxReportRowSection;

export function isTaxReportDataRow(r: TaxReportRow): r is TaxReportRowData {
  return r.kind === "data";
}

export function isTaxReportSectionRow(r: TaxReportRow): r is TaxReportRowSection {
  return r.kind === "section";
}

export type TaxReportDataPayload = {
  report_name: string;
  period: { start_date: string; end_date: string };
  branches: { id: number; name: string }[];
  rows: TaxReportRow[];
};

type TaxReportDataParams = IParams & { enabled?: boolean };

export const useGetTaxReportData = (params: TaxReportDataParams) =>
  useQuery<any, APIErrorResponse, { data: TaxReportDataPayload }>({
    queryKey: [
      "get-tax-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/tax-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetForecastReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-forecast-3rd-eye-report",
      params.start_date,
      params.end_date,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/forcast-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export const useGetAggregatorsReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-aggregators-3rd-eye-report",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/aggregator-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export type AggregatorsReportDataRow = {
  key: string;
  label: string;
  value_kind: "number" | "text";
  values: (number | string)[];
};

export type AggregatorsReportDataPayload = {
  company_name?: string | null;
  aggregators: { id: number; name: string }[];
  branch_names: string;
  start_date: string;
  end_date: string;
  report_title: string;
  compare_table_heading: string;
  rows: AggregatorsReportDataRow[];
};

type AggregatorsReportDataParams = IParams & { enabled?: boolean };

export const useGetAggregatorsReportData = (params: AggregatorsReportDataParams) =>
  useQuery<any, APIErrorResponse, { data: AggregatorsReportDataPayload }>({
    queryKey: [
      "get-aggregators-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/aggregator-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetPLReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-p-l-3rd-eye-report", params.start_date, params.end_date],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/profit-and-loss-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

interface ISuppParams extends IParams {
  supplier_id: string;
}

export const useGetSupplierReport = (params: ISuppParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-supplier-3rd-eye-report",
      params.start_date,
      params.end_date,
      params.supplier_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/supplier-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

interface IAccountParams extends IParams {
  account_id: string;
}

export const useGetAccountReport = (params: IAccountParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-account-3rd-eye-report",
      params.start_date,
      params.end_date,
      params.account_id,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/account-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export const useGetItemsReport = (
  params: IParams & {
    name?: string;
    sku?: string;
    method?: string;
    transaction_reference?: string;
  }
) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-items-3rd-eye-report",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.name ?? "",
      params.sku ?? "",
      params.method ?? "",
      params.transaction_reference ?? "",
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      if (params.name) searchParams.set("name", params.name);
      if (params.sku) searchParams.set("sku", params.sku);
      if (params.method && params.method !== "all") {
        searchParams.set("method", params.method);
      }
      if (params.transaction_reference) {
        searchParams.set("transaction_reference", params.transaction_reference);
      }
      return apiClient.get(
        `/api/thirdEyes/item-report?${searchParams.toString()}`,
        { responseType: "text" }
      );
    },
    enabled: false,
    gcTime: 0,
  });

export type ItemMovementReportColumn = {
  key: string;
  label: string;
};

export type ItemMovementReportRow = {
  item_name: string | null;
  sku: string | null;
  unit: string | null;
  branch: string | null;
  method?: string | null;
  transaction_type: string | null;
  transaction_reference: string | null;
  qty: number;
  cost: number;
  due_date: string | null;
  created_at: string | null;
  created_by: string | null;
};

export type ItemMovementReportDataPayload = {
  company_name?: string | null;
  report_title: string;
  branch_names: string;
  start_date: string;
  end_date: string;
  columns: ItemMovementReportColumn[];
  rows: ItemMovementReportRow[];
};

type ItemMovementReportDataParams = IParams & { enabled?: boolean };

/** JSON for Item Movement Report (item transaction history rows). */
export const useGetItemMovementReportData = (
  params: ItemMovementReportDataParams
) =>
  useQuery<any, APIErrorResponse, { data: ItemMovementReportDataPayload }>({
    queryKey: [
      "get-item-movement-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/item-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export type OrderSalesReportRow = {
  key: string;
  parent_key: string | null;
  depth: number;
  is_expandable: boolean;
  menu_id: number;
  name: string;
  sku: string;
  order_type: string;
  is_returned: boolean;
  row_kind: "product" | "modifier";
  net_quantity: number;
  net_sales: number;
  cost: number;
  cogs_percent: number;
};

export type OrderSalesReportDataPayload = {
  company_name?: string | null;
  report_name: string;
  period: { start_date: string | null; end_date: string | null };
  branch_names: string;
  rows: OrderSalesReportRow[];
};

type OrderSalesReportDataParams = IParams & {
  journey_id?: string;
  enabled?: boolean;
};

/** JSON for Order Sales Report (product mix from journey menu sales). */
export const useGetOrderSalesReportData = (params: OrderSalesReportDataParams) =>
  useQuery<any, APIErrorResponse, { data: OrderSalesReportDataPayload }>({
    queryKey: [
      "get-order-sales-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.journey_id ?? "",
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      if (params.journey_id) {
        searchParams.set("journey_id", params.journey_id);
      }
      return apiClient.get(
        `/api/thirdEyes/order-sales-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetOrderSalesReport = (params: OrderSalesReportDataParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-order-sales-report",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.journey_id ?? "",
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      if (params.journey_id) {
        searchParams.set("journey_id", params.journey_id);
      }
      return apiClient.get(
        `/api/thirdEyes/order-sales-report?${searchParams.toString()}`
      );
    },
    enabled: params.enabled ?? false,
    gcTime: 0,
  });

export const useGetAggsStatementReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-aggs-statement-3rd-eye-report",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/aggregator-statement-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export type AggregatorStatementReportColumn = {
  key: string;
  label: string;
};

export type AggregatorStatementReportRow = {
  aggregator_id: number;
  aggregator_name: string;
  payment_event_to_agg: number;
  payment_event_from_agg: number;
  net_sales_with_tax: number;
  aggregator_commission_pct: number;
  order_qty: number;
  total_commission_include_tax: number;
  aggregator_delivery_cost_with_tax: number;
  compensation: number;
  other_fee_with_tax: number;
  agg_bank_fee_with_tax: number;
};

export type AggregatorStatementReportDataPayload = {
  company_name?: string | null;
  branch_names: string;
  start_date: string;
  end_date: string;
  report_title: string;
  columns: AggregatorStatementReportColumn[];
  rows: AggregatorStatementReportRow[];
};

type AggregatorStatementReportDataParams = IParams & { enabled?: boolean };

export const useGetAggregatorStatementReportData = (
  params: AggregatorStatementReportDataParams
) =>
  useQuery<any, APIErrorResponse, { data: AggregatorStatementReportDataPayload }>({
    queryKey: [
      "get-aggregator-statement-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/aggregator-statement-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetBalanceSheetReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-balance-sheet-3rd-eye-report",
      params.start_date,
      params.end_date,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/balance-sheet-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

interface IPaymentTransParams extends IParams {
  payment_method_id: string;
  supplier_id?: string;
  vendor_id?: string;
  account_id?: string;
}

export const useGetPaymentTransactionsReport = (params: IPaymentTransParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-payment-transactions-3rd-eye-report",
      params.start_date,
      params.end_date,
      params.payment_method_id,
      (params.branches ?? []).join(","),
      params.include_company_level_payments === true ? "1" : "0",
      params.supplier_id ?? "",
      params.vendor_id ?? "",
      params.account_id ?? "",
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/payment-movement-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

export type PaymentMovementTransactionRow = {
  reference: string | null;
  branch: string | null;
  payment_method: string | null;
  period: string | null;
  period_display: string | null;
  payment_qty: string | number | null;
  net_payment: number;
  linked_to_aggregator: boolean;
  created_by: string | null;
  source: string | number | boolean | null;
  balance: number;
};

export type PaymentMovementReportColumn = {
  key: string;
  label: string;
};

export type PaymentMovementReportDataPayload = {
  company_name: string;
  payment_method_name?: string | null;
  start_date: string;
  end_date: string;
  opening_balance_label: string;
  /** Null when no payment method selected (preview state). */
  opening_balance: number | null;
  columns: PaymentMovementReportColumn[];
  transactions: PaymentMovementTransactionRow[];
};

type PaymentMovementReportDataParams = IParams & {
  payment_method_id?: string;
  supplier_id?: string;
  vendor_id?: string;
  account_id?: string;
  enabled?: boolean;
};

/** JSON for Payments Movement Report (opening balance + running balance rows). */
export const useGetPaymentMovementReportData = (
  params: PaymentMovementReportDataParams
) =>
  useQuery<any, APIErrorResponse, { data: PaymentMovementReportDataPayload }>({
    queryKey: [
      "payment-movement-report-data",
      params.start_date,
      params.end_date,
      params.payment_method_id,
      (params.branches ?? []).join(","),
      params.include_company_level_payments === true ? "1" : "0",
      params.supplier_id ?? "",
      params.vendor_id ?? "",
      params.account_id ?? "",
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      const pid = params.payment_method_id?.trim();
      if (pid) {
        searchParams.set("payment_method_id", pid);
      }
      const sid = params.supplier_id?.trim();
      if (sid) {
        searchParams.set("supplier_id", sid);
      }
      const vid = params.vendor_id?.trim();
      if (vid) {
        searchParams.set("vendor_id", vid);
      }
      const aid = params.account_id?.trim();
      if (aid) {
        searchParams.set("account_id", aid);
      }
      if (params.include_company_level_payments === true) {
        searchParams.set("include_company_level_payments", "1");
      }
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/payment-movement-report-data?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

interface IPriceFluctuationParams extends IParams {
  item_id: string;
}

export const useGetItemsPriceFluctuationReport = ({
  enabled,
  ...params
}: IPriceFluctuationParams & { enabled?: boolean }) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-items-price-fluctuation-3rd-eye-report",
      params.start_date,
      params.end_date,
      params.item_id,
      params.branches,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/price-fluctuation-report`, {
        params,
      });
    },
    enabled: enabled ?? false,
    gcTime: 0,
  });

export const useGetItemsFluctuationReport = ({
  enabled,
  ...params
}: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-items-fluctuation-3rd-eye-report",
      params.start_date,
      params.end_date,
      params.branches,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/item-fluctuation-report`, {
        params,
      });
    },
    enabled: enabled ?? false,
    gcTime: 0,
  });

/** Structured JSON for Top Items Fluctuation UI (same business logic as CSV export). */
export const useGetItemsFluctuationData = ({
  enabled,
  ...params
}: IParams) =>
  useQuery<any, APIErrorResponse, IItemFluctuationDataResponse>({
    queryKey: [
      "get-items-fluctuation-data",
      params.start_date,
      params.end_date,
      params.branches,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/thirdEyes/item-fluctuation-data?${searchParams.toString()}`
      );
    },
    enabled: enabled ?? false,
    gcTime: 0,
  });

export const useGetTrialBalanceReport = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-trial-balance-3rd-eye-report",
      params.start_date,
      params.end_date,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/thirdEyes/balancing-equation-report`, {
        params,
      });
    },
    enabled: false,
    gcTime: 0,
  });

type AccountStatementReportDataParams = IParams & {
  chart_account_id?: string;
  chart_account_placement_id?: string;
  chart_category_id?: string;
  // Request opening-balance row + running balance (sent as 1/0)
  has_balance?: boolean;
  enabled?: boolean;
};

export const useGetAccountStatementReportData = (
  params: AccountStatementReportDataParams
) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-account-statement-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.chart_account_id,
      params.chart_account_placement_id,
      params.chart_category_id,
      params.has_balance,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      if (params.chart_category_id) {
        searchParams.set("chart_category_id", params.chart_category_id);
      } else if (params.chart_account_id) {
        searchParams.set("chart_account_id", params.chart_account_id);
      }else if (params.chart_account_placement_id) {
        searchParams.set("chart_account_placement_id", params.chart_account_placement_id);
      }
      if (params.has_balance !== undefined) {
        searchParams.set("has_balance", params.has_balance ? "1" : "0");
      }
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/reports/account-statement?${searchParams.toString()}`
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetAccountStatementReportDownload = ({
  enabled,
  ...params
}: AccountStatementReportDataParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "get-account-statement-report-download",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.chart_account_id,
      params.chart_account_placement_id,
      params.chart_category_id,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      if (params.chart_category_id) {
        searchParams.set("chart_category_id", params.chart_category_id);
      } else if (params.chart_account_id) {
        searchParams.set("chart_account_id", params.chart_account_id);
      } else if (params.chart_account_placement_id) {
        searchParams.set(
          "chart_account_placement_id",
          params.chart_account_placement_id
        );
      }
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id))
      );
      return apiClient.get(
        `/api/reports/account-statement/download?${searchParams.toString()}`
      );
    },
    enabled: enabled ?? false,
    gcTime: 0,
  });
